import { useMemo } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { BadgeSheet } from "@/components/people/BadgeSheet"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  attendeeSetupQueryOptions,
  badgeSettingsQueryOptions,
  eventAttendeeFieldValuesQueryOptions,
  guestsQueryOptions,
  type Guest,
} from "@/core/people"

// Badge design is a full-screen designer, so it lives beside /badges (the desk
// workflow: pick people, print, mark printed) rather than in the Settings
// suite — the design it edits is still per-event config everyone inherits.
//
// The editor is the same BadgeSheet the print sheet uses, in `preview` mode so
// a test print never stamps badgePrintedAt, and `embedded` so it fills the
// canvas.

export const Route = createFileRoute("/$orgSlug/$eventSlug/badge-design")({
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(badgeSettingsQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(attendeeSetupQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(eventAttendeeFieldValuesQueryOptions(eventId))
        .catch(() => undefined)
      // Only for the tier names offered in the designer — the preview cards
      // themselves are never real guests.
      void context.queryClient
        .ensureQueryData(guestsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: BadgeDesignSettings,
})

function BadgeDesignSettings() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/badge-design" })
  const navigate = useNavigate()
  const { eventId, row: eventRow } = useActiveEvent()

  const guestsQ = useQuery({ ...guestsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const setupQ = useQuery({ ...attendeeSetupQueryOptions(eventId ?? ""), enabled: !!eventId })
  const valuesQ = useQuery({
    ...eventAttendeeFieldValuesQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const settingsQ = useQuery({
    ...badgeSettingsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const attendeeFields = setupQ.data?.fields ?? []

  const valuesByGuest = useMemo(() => {
    const optionLabel = new Map(
      attendeeFields.flatMap((f) => (f.options ?? []).map((o) => [`${f.id}:${o.value}`, o.label])),
    )
    const map = new Map<string, Record<string, string>>()
    for (const v of valuesQ.data?.data ?? []) {
      if (v.value == null || v.value === "") continue
      const rec = map.get(v.guestId) ?? {}
      rec[v.fieldId] = optionLabel.get(`${v.fieldId}:${v.value}`) ?? v.value
      map.set(v.guestId, rec)
    }
    return map
  }, [attendeeFields, valuesQ.data])

  const tierNames = useMemo(
    () =>
      [...new Set((guestsQ.data ?? []).map((g) => g.ticketTypeName).filter(Boolean))] as string[],
    [guestsQ.data],
  )

  // Preview material is ALWAYS these two demo people, never the real roster:
  // every field is populated (so nothing is invisible while you design) and the
  // second card is a deliberate worst case — long name, long title, long
  // company — so overflow shows up here rather than at the printer.
  const previewRows = useMemo<Guest[]>(() => {
    const base = {
      eventId: eventId ?? "",
      organizationId: "",
      preApproved: 1,
      status: "confirmed",
      guestCount: 0,
      maxGuests: 0,
      createdAt: "",
    }
    return [
      {
        ...base,
        id: "sample-short",
        firstName: "Ava",
        lastName: "Ng",
        nameAtInvite: "Ava Ng",
        emailAtInvite: "ava@example.com",
        titleAtInvite: "CEO",
        companyAtInvite: "Kite",
        ticketTypeName: tierNames[0] ?? "VIP",
      } as Guest,
      {
        ...base,
        id: "sample-long",
        firstName: "Maximiliana",
        lastName: "Featherstonehaugh-Ruiz",
        nameAtInvite: "Maximiliana Featherstonehaugh-Ruiz",
        emailAtInvite: "maximiliana@example.com",
        titleAtInvite: "Senior Vice President, Global Partnerships & Alliances",
        companyAtInvite: "Northwind Traders International Holdings",
        ticketTypeName: tierNames[1] ?? tierNames[0] ?? "General Admission",
      } as Guest,
    ]
  }, [eventId, tierNames])

  return (
    <BadgeSheet
      rows={previewRows}
      eventName={eventRow?.name ?? ""}
      guestUrl={(g) =>
        `${window.location.origin}/${params.orgSlug}/${params.eventSlug}/people/${g.id}`
      }
      fields={attendeeFields}
      valuesByGuest={valuesByGuest}
      settings={settingsQ.data ?? null}
      preview
      embedded
      // Close = back to the desk workflow this design feeds.
      onClose={() => void navigate({ to: "/$orgSlug/$eventSlug/badges", params })}
    />
  )
}
